// Auto Title Filter status — pinned on the Scanner page (and used inside
// Settings). Two render modes:
//
//   1. Subtle one-line indicator (existing behaviour) when the row exists
//      and the filter is active. Shows anchor count + a "view" link.
//
//   2. Prominent yellow banner (new in Phase 5) when readiness is false:
//      cv_summary / domain_anchors (≥3) / seniority_level / years_experience
//      not all populated. The banner blocks scan dispatch upstream — the
//      Scanner page disables Run scan / Full run buttons whenever this
//      banner is showing — and points the user at /settings/filter to
//      re-extract or fill manually.
//
// Old user_title_filter rows that pre-date the readiness check still
// land in this banner mode if seniority wasn't populated (extraction
// runs before Pass 3 shipped). The Re-extract button on /settings/filter
// is the fix.

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api';
import type { TitleFilterState } from '../../lib/types';

const MISSING_LABELS: Record<TitleFilterState['missing'][number], string> = {
  cv_summary: 'CV summary',
  domain_anchors: 'anchor keywords',
  seniority_level: 'seniority level',
  years_experience: 'years of experience',
};

export default function TitleFilterIndicator() {
  const nav = useNavigate();
  const stateQ = useQuery({
    queryKey: ['title-filter'],
    queryFn: () => apiGet<TitleFilterState>('/api/title-filter'),
    staleTime: 30_000,
  });
  const data = stateQ.data;

  // ── Banner mode: title filter incomplete → block scans ─────────────
  if (data && !data.ready) {
    const missingNames = data.missing.map((k) => MISSING_LABELS[k] || k);
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
          padding: '14px 18px',
          marginBottom: 16,
          borderRadius: 8,
          border: '1px solid rgba(170,140,30,0.35)',
          background: 'rgba(170,140,30,0.08)',
          color: '#7a5e10',
        }}
      >
        <div style={{ fontSize: 22, lineHeight: 1, marginTop: 2 }}>⚠</div>
        <div style={{ flex: 1, fontSize: 13, lineHeight: 1.55 }}>
          <div style={{ fontWeight: 600, color: '#5a4408', marginBottom: 4 }}>
            Title filter incomplete — scans are paused.
          </div>
          <div>
            Without these, the CV-derived gate falls open and irrelevant
            roles leak into your matches. Missing:&nbsp;
            <strong>{missingNames.join(', ')}</strong>.
          </div>
        </div>
        <button
          className="btn"
          onClick={() => nav('/settings/filter')}
          style={{
            fontSize: 12,
            padding: '6px 14px',
            background: '#7a5e10',
            color: '#fff',
            border: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          Open settings →
        </button>
      </div>
    );
  }

  // ── Subtle mode: row exists + ready → one-line status ──────────────
  if (!data || !data.exists) return null;
  return (
    <div
      style={{
        fontSize: 11.5,
        color: 'var(--ink3)',
        marginBottom: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <span>
        {data.active
          ? `Auto title filter active · ${data.domainAnchors.length} anchor${data.domainAnchors.length === 1 ? '' : 's'} from your CV`
          : data.userDisabled
            ? 'Auto title filter disabled'
            : 'Auto title filter inactive (extraction pending)'}
      </span>
      <button
        type="button"
        onClick={() => nav('/settings/filter')}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          color: 'var(--ink2)',
          textDecoration: 'underline',
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        view
      </button>
    </div>
  );
}
